import { LAST_HEARD_AFTER_MINS } from './adapter';
import type { Cart } from './types';

/** Battery thresholds (unplugged carts only). Configurable; handoff defaults 20/10. */
export const WARN_THRESHOLD = 20;
export const CRIT_THRESHOLD = 10;

const C = {
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  sub: 'var(--sub)',
  muted: 'var(--muted)',
  ink: 'var(--ink)',
  grayMark: 'var(--stale-mark)',
  grayText: 'var(--stale-text)',
};

/**
 * "Last heard" formatting: ≤90 min → minutes; <24h → whole hours; <7 days →
 * days; beyond → absolute date. Round down. Never seconds, never live-ticking.
 */
export function formatLastHeard(mins: number): string {
  if (mins <= 90) return `Last heard ${mins} min ago`;
  if (mins < 1440) return `Last heard ${Math.floor(mins / 60)} hr ago`;
  if (mins < 10080) {
    const d = Math.floor(mins / 1440);
    return `Last heard ${d} ${d === 1 ? 'day' : 'days'} ago`;
  }
  const date = new Date(Date.now() - mins * 60000);
  return `Last heard ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

/** Avatar initials from a display name. */
export function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Drop all-zero hyphen segments for display; stored serial never altered. */
export function formatSerial(serial: string): string {
  return serial
    .split('-')
    .filter((seg) => !(seg.length > 1 && /^0+$/.test(seg)))
    .join('-');
}

export interface CartView {
  serial: string;
  /** Inner fill bar color */
  battFill: string;
  /** Battery outline + nub color */
  battBorder: string;
  pctColor: string;
  pinColor: string;
  triColor: string;
  chkColor: string;
  /** "Last heard…" line; null until the cart is 24 h silent */
  lastHeard: string | null;
  lastHeardColor: string;
  gray: boolean;
}

/**
 * Per-cart display logic from the handoff's design rules: battery thresholds
 * color the icon only (unplugged carts only); staleness grays all four marks
 * and overrides everything including a known error. From 24 h silent a
 * quiet gray "Last heard" line shows while the marks stay in color.
 */
export function cartView(
  cart: Cart,
  warn: number = WARN_THRESHOLD,
  crit: number = CRIT_THRESHOLD
): CartView {
  const isCrit = cart.pct <= crit && !cart.plugged;
  const isWarn = !isCrit && cart.pct <= warn && !cart.plugged;
  const out = cart.fence === 'out';
  const gray = cart.conn !== 'ok';
  const off = cart.conn === 'off';
  const quiet = cart.minsSinceHeard !== null && cart.minsSinceHeard >= LAST_HEARD_AFTER_MINS;
  const tone = isCrit ? C.danger : isWarn ? C.warn : C.ok;

  const view: CartView = {
    serial: formatSerial(cart.serial),
    battFill: tone,
    battBorder: isCrit || isWarn ? tone : C.muted,
    pctColor: isCrit || isWarn ? tone : C.sub,
    pinColor: out ? C.danger : C.ok,
    triColor: C.danger,
    chkColor: C.ok,
    lastHeard: quiet ? formatLastHeard(cart.minsSinceHeard!) : null,
    lastHeardColor: C.grayText,
    gray,
  };

  if (gray) {
    view.battFill = C.grayMark;
    view.battBorder = C.grayMark;
    view.pctColor = C.grayText;
    view.pinColor = C.grayMark;
    view.triColor = C.grayMark;
    view.chkColor = C.grayMark;
    view.lastHeard = cart.minsSinceHeard !== null ? formatLastHeard(cart.minsSinceHeard) : null;
    view.lastHeardColor = off ? C.danger : C.grayText;
  }

  return view;
}

export interface DetailView {
  serial: string;
  battFill: string;
  battBorder: string;
  /** Hero percentage text color */
  heroColor: string;
  pinColor: string;
  zoneText: string;
  zoneColor: string;
  triColor: string;
  chkColor: string;
  errText: string;
  errColor: string;
  lastHeard: string | null;
  lastHeardColor: string;
}

/** Detail screen: same facts, larger. Staleness grays marks and value text. */
export function detailView(
  cart: Cart,
  warn: number = WARN_THRESHOLD,
  crit: number = CRIT_THRESHOLD
): DetailView {
  const isCrit = cart.pct <= crit && !cart.plugged;
  const isWarn = !isCrit && cart.pct <= warn && !cart.plugged;
  const out = cart.fence === 'out';
  const gray = cart.conn !== 'ok';
  const off = cart.conn === 'off';
  const quiet = cart.minsSinceHeard !== null && cart.minsSinceHeard >= LAST_HEARD_AFTER_MINS;
  const tone = isCrit ? C.danger : isWarn ? C.warn : C.ok;
  const thresholded = isCrit || isWarn;

  return {
    serial: formatSerial(cart.serial),
    battFill: gray ? C.grayMark : thresholded ? tone : C.ok,
    battBorder: gray ? C.grayMark : thresholded ? tone : C.muted,
    heroColor: gray ? C.grayText : thresholded ? tone : C.ink,
    pinColor: gray ? C.grayMark : out ? C.danger : C.ok,
    zoneText: out ? 'Out of fence' : 'In fence',
    zoneColor: gray ? C.grayText : out ? C.danger : C.ok,
    triColor: gray ? C.grayMark : C.danger,
    chkColor: gray ? C.grayMark : C.ok,
    errText: cart.err ? 'Error' : 'No errors',
    errColor: gray ? C.grayText : cart.err ? C.danger : C.ok,
    lastHeard: (gray || quiet) && cart.minsSinceHeard !== null ? formatLastHeard(cart.minsSinceHeard) : null,
    lastHeardColor: off ? C.danger : C.grayText,
  };
}
